import { useMutation } from '@tanstack/react-query';
import type {
  InviteAccept,
  LoginResult,
  PasswordResetConfirm,
  PasswordResetRequest,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';

/**
 * Account-recovery mutations — the three pre-session auth endpoints behind the invite/forgot/reset
 * pages. TanStack `useMutation` over the generated client, mirroring data/integrations.ts. These run
 * ANONYMOUSLY (no session yet): each resolves on the API's system pool and is rate-limited there, so
 * the UI gates its submit button on `isPending` and surfaces 4xx faithfully via ApiClientError.
 *
 * Invite-accept either ESTABLISHES a session (the API regenerates the session cookie and returns the
 * SessionUser — the page re-reads the principal via auth's `refresh()` and lands in the app) OR, when
 * the linked account is already TOTP-enrolled, returns a `twoFactorRequired` step-up challenge (no
 * session yet) that the page completes with a code via auth's `verifyTwoFactor` — exactly like /login.
 * Reset-request is deliberately neutral (always 200 {ok:true}, never reveals whether an account
 * exists) and reset-confirm does NOT auto-login (the user signs in afresh with their new password).
 */

/** Accept a staff invite: set name + password, link the account, and start a session (or a 2FA challenge). */
export function useAcceptInvite() {
  return useMutation({
    mutationFn: async (body: InviteAccept): Promise<LoginResult> =>
      unwrap(await api.POST('/v1/auth/invite/accept', { body })),
  });
}

/**
 * Request a password-reset email. Always resolves 200 {ok:true} regardless of whether the email is
 * known — the page must show the same neutral message either way (no account enumeration).
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (body: PasswordResetRequest): Promise<{ ok: true }> =>
      unwrap(await api.POST('/v1/auth/password/reset-request', { body })),
  });
}

/** Confirm a password reset with the emailed token. Does NOT auto-login — caller signs in afresh. */
export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: async (body: PasswordResetConfirm): Promise<{ ok: true }> =>
      unwrap(await api.POST('/v1/auth/password/reset-confirm', { body })),
  });
}
